<template>
  <CustomModal ref="modal" title="Modifier l'encaissement" class="edit-collection-modal">
    <div class="modal-content">
      <p class="recap">
        <span class="recap-amount">{{ formattedAmount }}</span>
        <span v-if="contractLabel" class="recap-contract">{{ contractLabel }}</span>
      </p>
      <CustomForm ref="form" :inputs=inputs :submit=buttons.submit @submit="preventNativeSubmit">
        <div class="fields-row">
          <CustomTextField class="date" :content=inputs.date />
          <CustomSelectField class="payment-type" :content=inputs.paymentType />
        </div>
        <div class="fields-row">
          <CustomSelectField class="select" :content=inputs.paymentPurpose />
          <CustomSelectField v-if="isCreditNotePurpose" class="select" :content=inputs.creditNoteNumber />
        </div>
        <p class="note">Le montant et le contrat ne peuvent pas être modifiés : pour les corriger, ressaisissez l'encaissement.</p>
        <div class="button-container">
          <CustomButton class="button" @click="submitForm" :params=buttons.submit />
          <CustomButton class="button" :is-secondary="true" @click="closeModal()" :params=buttons.cancel />
        </div>
      </CustomForm>
    </div>
  </CustomModal>
</template>

<script>
import CustomButton from "@/components/forms/customs/customButton/CustomButton.vue";
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomSelectField from "@/components/forms/customs/customSelectField/CustomSelectField.vue";
import {NotificationService} from "@vues/v-common";
import {PAYMENT_TYPE} from "@/models/paymentType";
import {PAYMENT_PURPOSE} from "@/models/paymentPurpose";
import {CollectionController} from "@/controllers/business/CollectionController";
import {BusinessController} from "@/controllers/business/BusinessController";
import CustomForm from "@/components/forms/CustomForm.vue";
import CustomModal from "@/components/modals/CustomModal.vue";
import {convertISOToDate} from "@/utils";

// Modale de modification d'un encaissement : date, mode de paiement et motif.
// À ouvrir avec openModal(encaissement) ; callback est appelé après l'enregistrement.
export default {
  name: 'EditCollectionModal',
  props: ['callback'],
  components: {CustomModal, CustomForm, CustomSelectField, CustomTextField, CustomButton},
  data() {
    return {
      collectionId: undefined,
      amount: 0,
      contractLabel: '',
      inputs: {
        date: {label: 'Date', value: '', type: 'date'},
        paymentType: {label: 'Mode de paiement', value: PAYMENT_TYPE.TRANSFER.value, type: 'select', selectValues: [PAYMENT_TYPE.CHECK, PAYMENT_TYPE.CASH, PAYMENT_TYPE.TRANSFER]},
        paymentPurpose: {label: 'Motif', value: PAYMENT_PURPOSE.INVOICE.value, type: 'select', selectValues: [PAYMENT_PURPOSE.INVOICE, PAYMENT_PURPOSE.DEPOSIT, PAYMENT_PURPOSE.CREDIT_NOTE]},
        creditNoteNumber: {label: 'Avoir', value: undefined, type: 'select', selectValues: []},
      },
      buttons: {
        submit: {text: "Enregistrer"},
        cancel: {text: "Annuler"},
      }
    };
  },
  computed: {
    isCreditNotePurpose() {
      return this.inputs.paymentPurpose.value === PAYMENT_PURPOSE.CREDIT_NOTE.value;
    },
    formattedAmount() {
      return (Number(this.amount) || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + ' €';
    }
  },
  methods: {
    async openModal(collection) {
      this.collectionId = collection.id || collection._id;
      this.amount = collection.amount;
      this.contractLabel = collection.documentRef && collection.documentRef !== '-' ? 'Pièce ' + collection.documentRef : '';
      this.inputs.date.value = convertISOToDate(collection.date);
      this.inputs.paymentType.value = collection.paymentType || PAYMENT_TYPE.TRANSFER.value;
      // Un ancien encaissement sans motif est considéré comme un encaissement de facture
      this.inputs.paymentPurpose.value = Object.values(PAYMENT_PURPOSE).some(purpose => purpose.value === collection.paymentPurpose)
          ? collection.paymentPurpose
          : PAYMENT_PURPOSE.INVOICE.value;
      this.inputs.creditNoteNumber.selectValues = [];
      this.inputs.creditNoteNumber.value = collection.creditNoteNumber;
      this.$refs.modal.openModal();

      // Les avoirs du contrat, pour le motif « Avoir »
      const business = await BusinessController.get(collection.businessId);
      if (business && !business.errors) {
        const creditNotes = business.creditNotes || [];
        this.inputs.creditNoteNumber.selectValues = creditNotes.map(creditNote => ({
          label: 'Avoir AV' + String(creditNote.number).padStart(4, '0'),
          value: creditNote.number
        }));
        if (this.inputs.creditNoteNumber.value === undefined && creditNotes.length === 1) {
          this.inputs.creditNoteNumber.value = creditNotes[0].number;
        }
      }
    },
    closeModal(event = false) {
      this.$refs.modal?.closeModal(event);
    },
    preventNativeSubmit(event) {
      event?.preventDefault?.();
    },
    async submitForm(event) {
      this.preventNativeSubmit(event);
      if (!this.inputs.date.value) {
        this.inputs.date.error = true;
        (new NotificationService).addNotification("danger", "La date n'est pas renseignée");
        return;
      }

      if (this.isCreditNotePurpose && this.inputs.creditNoteNumber.value === undefined) {
        this.inputs.creditNoteNumber.error = true;
        (new NotificationService).addNotification("danger", "L'avoir concerné n'est pas renseigné");
        return;
      }

      const res = await this.$refs.form.submitForm(() => CollectionController.patch(this.collectionId, {
        date: this.inputs.date.value,
        paymentType: this.inputs.paymentType.value,
        paymentPurpose: this.inputs.paymentPurpose.value,
        creditNoteNumber: this.isCreditNotePurpose ? this.inputs.creditNoteNumber.value : undefined,
      }));
      if (!res.errors) {
        (new NotificationService).addNotification("success", "Encaissement modifié");
        this.closeModal(true);
        if (this.callback) this.callback();
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

// Pas de classe « container » ici : le style scoped du parent s'appliquerait à la racine de CustomSelectField (qui porte cette classe) et décalerait les listes déroulantes
.modal-content
  display flex
  flex-direction column
  gap 10px

  .recap
    display flex
    align-items baseline
    gap 10px
    margin 0

    .recap-amount
      font-size 1.3em
      font-weight bold

    .recap-contract
      color #6b7076

  form
    display flex
    flex-direction column
    gap 10px

    .fields-row
      display grid
      grid-template-columns 1fr 1fr
      gap 12px

      .field
        min-width 0
        width 100%

    .note
      margin 0
      font-size 12px
      color #6b7076

    // Le bouton principal est en premier dans le DOM (action par défaut de la touche Entrée),
    // l'ordre visuel est inversé : [Annuler] [Enregistrer]
    .button-container
      display flex
      flex-direction row-reverse
      justify-content flex-start
      gap 10px
      margin-top 4px
      .button
        min-width 110px

@media (max-width 480px)
  .modal-content form .fields-row
    grid-template-columns 1fr
</style>

<style lang="stylus">
@require '~/src/common/style/common.styl';
.edit-collection-modal
  > .modal
    width 500px !important
    height auto !important
    max-height 90vh
</style>
